/**
 * monEntrepriseClient.js
 * Évalue les expressions publicodes localement, via le modèle social officiel
 * (mêmes règles que l'API mon-entreprise.urssaf.fr). Aucun appel réseau :
 * plus de CORS ni de rate limit (429).
 */

// Le moteur Publicodes et le modèle social pèsent plusieurs centaines de Ko.
// On les charge dynamiquement au premier calcul (hors bundle initial) et on
// mémoïse l'instance : le parsing des règles ne se fait qu'une seule fois.
let enginePromise = null;

async function getEngine() {
  if (!enginePromise) {
    enginePromise = Promise.all([import('publicodes'), import('modele-social')]).then(
      ([{ default: Engine }, { default: rules }]) => new Engine(rules)
    );
  }
  return enginePromise;
}

// L'ancienne API attendait les montants sous forme { valeur, unité } ;
// le moteur Publicodes attend une chaîne "valeur unité". Les autres valeurs
// (énumérations "'EI'", booléens "oui"/"non") sont passées telles quelles.
function toPublicodesSituation(situation) {
  const result = {};
  for (const [key, value] of Object.entries(situation)) {
    result[key] =
      value && typeof value === 'object' && 'valeur' in value
        ? `${value.valeur} ${value.unité ?? ''}`.trim()
        : value;
  }
  return result;
}

/**
 * Évalue des expressions publicodes localement via le modèle social officiel.
 *
 * @param {string[]} expressions - Règles à calculer
 * @param {object} situation - Variables d'entrée (règle → { valeur, unité } ou valeur brute)
 * @returns {Promise<number[]>} Valeurs calculées, dans l'ordre des expressions
 */
export async function evaluateMonEntreprise(expressions, situation) {
  // shallowCopy() isole la situation de chaque appel (les calculs des statuts
  // tournent en parallèle via Promise.all) sans re-parser les règles.
  const engine = (await getEngine()).shallowCopy();
  engine.setSituation(toPublicodesSituation(situation));
  return expressions.map((expression) => engine.evaluate(expression).nodeValue);
}
